import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgbCarousel, NgbSlideEvent } from '@ng-bootstrap/ng-bootstrap';
import {
  AppConfig,
  BannerCloseReason,
  BannerOrientation,
  BannerPopupEvent,
  BannerPopupResult,
} from 'src/app/shared/constants/global-config.constant';
import { IBannerCampaign } from 'src/app/shared/interfaces/interface';

/** One picture in the carousel, after choosing the orientation. */
export interface BannerPopupSlide {
  campaign_id: number;
  slide_id: number;
  /** Position inside its own campaign (what the API reports as slide_index). */
  slide_index: number;
  url: string;
}

const SLIDE_ID_PREFIX = 'banner-';

/**
 * Flattens the active campaigns (already ordered by the API) into the pictures to show
 * on a kiosk of this orientation. A portrait kiosk skips slides that have no portrait
 * image; a landscape kiosk always has one, the landscape image is mandatory.
 */
export function flattenBannerSlides(
  campaigns: IBannerCampaign[] | null | undefined,
  orientation: BannerOrientation,
): BannerPopupSlide[] {
  const out: BannerPopupSlide[] = [];
  for (const campaign of campaigns ?? []) {
    (campaign.slides ?? []).forEach((slide, index) => {
      const url = orientation === 'portrait' ? slide.portrait_url : slide.landscape_url;
      if (url) {
        out.push({ campaign_id: campaign.id, slide_id: slide.id, slide_index: index, url });
      }
    });
  }
  return out;
}

/**
 * A random UUID v4 for one popup opening (the API column is char(36)). Hand-rolled on
 * crypto.getRandomValues because crypto.randomUUID is undefined on the http CefSharp origin
 * and the guid-typescript package is CommonJS (build optimisation bailout).
 */
export function newBannerImpressionId(): string {
  const bytes = new Uint8Array(16);
  const cryptoObj: Crypto | undefined = (typeof crypto !== 'undefined') ? crypto : undefined;
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    cryptoObj.getRandomValues(bytes);
  }
  else {
    for (let i = 0; i < 16; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** The index encoded in an ngbSlide id, or -1. */
export function parseBannerSlideIndex(slideId: string | null | undefined): number {
  if (!slideId || !slideId.startsWith(SLIDE_ID_PREFIX)) {
    return -1;
  }
  const n = Number(slideId.slice(SLIDE_ID_PREFIX.length));
  return Number.isInteger(n) && n >= 0 ? n : -1;
}

/**
 * The /home promotional banner popup: a carousel of campaign pictures inside an NgbModal.
 *
 * It owns nothing but the pictures and the gestures. The host (HomeComponent) opens the
 * modal, sends the events and closes it on the (onClose) output - this component never
 * calls the API, so it can be rendered anywhere (e.g. tests) without a session.
 *
 * Why these carousel settings: ng-bootstrap pauses on hover and on focus by default, and
 * on a touchscreen a tap leaves a sticky mouseenter while a dot tap focuses the carousel,
 * so without pauseOnHover/pauseOnFocus false the pictures would stop advancing after the
 * first touch. Arrows are off (clumsy next to the close button); swipe is handled here
 * because the app has no HammerJS.
 *
 * The picture itself is deliberately not clickable (no cursor:pointer, no handler), so the
 * ClickDiagnosticService does not judge taps on it: tapping the picture is a no-op by design.
 */
@Component({
  selector: 'app-banner-popup',
  templateUrl: './banner-popup.component.html',
  styleUrls: ['./banner-popup.component.scss'],
})
export class BannerPopupComponent implements OnInit {
  @Input() campaigns: IBannerCampaign[] = [];
  @Input() orientation: BannerOrientation = 'landscape';

  @Output() onClose: EventEmitter<BannerPopupResult> = new EventEmitter<BannerPopupResult>();
  /** Index into `slides` of the picture that just became visible. */
  @Output() onSlideView: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild('carousel') carousel: NgbCarousel;

  slides: BannerPopupSlide[] = [];
  interval = AppConfig.BANNER_SLIDE_INTERVAL_MS;
  slideIdPrefix = SLIDE_ID_PREFIX;
  BannerCloseReason = BannerCloseReason;

  private touchStartX: number | null = null;

  ngOnInit() {
    this.slides = flattenBannerSlides(this.campaigns, this.orientation);
  }

  close(reason: BannerCloseReason) {
    this.onClose.emit({ event: BannerPopupEvent.CLOSE, reason });
  }

  onSlide(event: NgbSlideEvent) {
    const index = parseBannerSlideIndex(event?.current);
    if (index >= 0) {
      this.onSlideView.emit(index);
    }
  }

  onTouchStart(event: TouchEvent) {
    this.touchStartX = event.touches && event.touches.length > 0 ? event.touches[0].clientX : null;
  }

  onTouchEnd(event: TouchEvent) {
    if (this.touchStartX === null) {
      return;
    }
    const endX = event.changedTouches && event.changedTouches.length > 0
      ? event.changedTouches[0].clientX
      : this.touchStartX;
    const delta = endX - this.touchStartX;
    this.touchStartX = null;
    this.swipe(delta);
  }

  /** Negative delta = finger moved left = next slide. Exposed for tests. */
  swipe(deltaX: number) {
    if (this.slides.length < 2 || !this.carousel) {
      return;
    }
    if (deltaX <= -AppConfig.BANNER_SWIPE_THRESHOLD_PX) {
      this.carousel.next();
    }
    else if (deltaX >= AppConfig.BANNER_SWIPE_THRESHOLD_PX) {
      this.carousel.prev();
    }
  }
}
